import { useTranslation } from 'react-i18next'
import type { HealthStatus } from '@/services/api/extensions'
import { cn } from '@/utils/cn'

interface Props {
  status: HealthStatus | 'unknown'
  className?: string
  /** Render label text next to the dot. Defaults to true. */
  showLabel?: boolean
}

const DOT_CLASS: Record<Props['status'], string> = {
  healthy: 'bg-success',
  degraded: 'bg-warning',
  unreachable: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
}

const LABEL_KEY: Record<Props['status'], string> = {
  healthy: 'extensions.health.healthy',
  degraded: 'extensions.health.degraded',
  unreachable: 'extensions.health.unreachable',
  unknown: 'extensions.health.unknown',
}

/**
 * Service reachability indicator. Visually distinct from any user-controlled
 * toggle — never overload one onto the other. Uses a dot glyph + the
 * "Healthy / Degraded / Unreachable" label vocabulary.
 */
export function HealthDot({ status, className, showLabel = true }: Props) {
  const { t } = useTranslation()
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 text-xs text-muted-foreground', className)}
    >
      <span className={cn('inline-block size-2 rounded-full', DOT_CLASS[status])} aria-hidden />
      {showLabel && <span>{t(LABEL_KEY[status])}</span>}
    </span>
  )
}
